import { Chip } from 'primereact/chip';
import { CSSProperties } from 'react';
import { useTranslation } from "react-i18next";

import "./StatusChip.scss";

export interface StatusColor {
    background: string;
    text: string;
}

// Background and text color used by status
export const StatusColors: Record<string, StatusColor> = {
    "red": {background: "#fbeef0", text: "#d32d41"},
    "blue": {background: "#f1f8fe", text: "#08365e"},
    "green": {background: "#e8f3ec", text: "#56a476"},
    "grey": {background: "#dee2e6", text: "#495057"},
    "yellow": {background: "#ffefd5", text: "#ffad20"},
};

// Color name (from `StatusColors`) assigned to a status
export const StatusToColorName: Record<string, string> = {
    // Flow (FlowPhase and FlowEnginePhase)
    "failed": "red",
    "running": "blue",
    "succeeded": "green",
    "initializing": "blue",
    "canceled": "grey",
    "terminated": "grey",
    "pending": "grey",
    "waiting": "grey", // when there is no active workflow for a given cron job
    "sanitizationfailed": "red",
	"empty": "grey",

    // Pods / Containers
    "init-imagepullbackoff": "red",
    "containercreating": "blue",
    "init-crashloopbackoff": "red",
    "containerstatusunknown": "grey",
    "error": "red",
    "crashloopbackoff": "red",
    "completed": "green",
};

// Make status safe to compare and use with i18n
// (by lowering it + removing ':' + adding a default value)
export const sanitizeStatus= (status: string) => status.toLowerCase().replace(":", "-") || "empty";

// Return color corresponding to a status (or grey if status is unknown)
export const getStatusColor = (status: string): StatusColor => {
  let colorName = StatusToColorName[status] || "grey";
  return StatusColors[colorName];
}

export interface StatusChipProps {
  status: string
  prefix?: string
  styles?: CSSProperties
  className?: string
}

// Render a small status chip with the color asociated to the status
export const StatusChip = ({status, prefix = "", styles = {}, className = ""}: StatusChipProps) => {
  const { t } = useTranslation();
  status = sanitizeStatus(status);

  return (
    <Chip
      label={prefix + (t("kube-status." + status))}
      className={"statusChip" + " " + className}
      style={{...styles, background: getStatusColor(status).background, color: getStatusColor(status).text}}
    />
  );
}
